import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Paso plegable, con la misma cara que los pasos de la página pública
 * (components/business/PasoSeccion.jsx): círculo numerado en el color del
 * negocio, título y descripción.
 *
 * La diferencia es que este se abre y se cierra. Se usa en acordeón —abrir uno
 * cierra el anterior— para que un formulario largo se vea de a una cosa por vez
 * en lugar de como una tirada de campos.
 *
 * Es controlado: quién está abierto lo decide el padre, que es lo que permite
 * que abrir uno cierre al otro. Cuando está cerrado muestra un resumen de lo
 * cargado, así se ve de un vistazo qué falta sin tener que abrirlo.
 *
 * COLOR DE LOS ESTADOS. Son tres y cada uno tiene el suyo:
 *
 *   naranja (el de la marca) — el paso abierto, o sea dónde estás parado
 *   verde                    — el paso ya completo
 *   gris                     — el que todavía no tocaste
 *
 * El verde es la excepción a la regla de que el color sale de la marca, y va
 * por lo mismo que el semáforo de estado de CurrentPlan: acá el color informa,
 * no decora. Antes el tilde de "listo" salía en el mismo naranja que el paso
 * abierto, así que los dos estados que más importa distinguir —"esto ya está" y
 * "esto es lo que estás haciendo"— se veían iguales, y el tilde era la única
 * pista, a 16px.
 */
export default function PasoPlegable({
  numero,
  // Alternativa al número, para las secciones que se identifican con un ícono
  // en vez de con un orden —la configuración del negocio, donde no hay un
  // "primero" y un "después"—.
  icono,
  titulo,
  descripcion,
  resumen,
  completo = false,
  abierto,
  onToggle,
  children,
}) {
  const idContenido = `plegable-${numero ?? String(titulo).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const menosMovimiento = useReducedMotion();

  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl border bg-white transition-colors',
        // El abierto manda sobre el completo: si lo estás editando, el borde
        // marca dónde estás aunque el paso ya esté listo.
        abierto
          ? 'border-primary/40 shadow-sm'
          : completo
          ? 'border-emerald-200 hover:border-emerald-300'
          : 'border-neutral-200 hover:border-neutral-300'
      )}
    >
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={abierto}
          aria-controls={idContenido}
          className="flex w-full items-center gap-3 p-4 text-left sm:p-5"
        >
          {/* Acá el orden es al revés que en el borde: completo gana sobre
              abierto. El círculo dice QUÉ es el paso —listo o pendiente— y eso
              no deja de ser cierto porque lo abras para revisarlo. */}
          <span
            className={cn(
              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors',
              completo
                ? 'bg-emerald-500 text-white'
                : abierto
                ? 'bg-primary text-primary-foreground'
                : 'bg-neutral-100 text-neutral-500'
            )}
            aria-hidden="true"
          >
            {/* El tilde se muestra siempre que el paso esté completo, también
                abierto: antes al abrir un paso ya terminado el tilde se iba y
                volvía el número, y parecía que abrirlo lo había descompletado. */}
            {completo ? <Check className="h-4 w-4" strokeWidth={3} /> : (icono ?? numero)}
          </span>

          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-neutral-900">
              {titulo}
              {/* "Completo" es un color y un tilde, y el círculo va aria-hidden:
                  sin esto, quien no ve la pantalla no tiene forma de saber qué
                  pasos ya están listos. */}
              {completo && <span className="sr-only"> (completo)</span>}
            </span>
            {/* Cerrado muestra lo cargado; abierto, la ayuda del paso.
                Cerrado va siempre a una línea, se trate del resumen o de la
                ayuda: una descripción larga hacía crecer la cabecera a dos y
                tres renglones, y con cinco pasos cerrados eso es media pantalla
                de texto que nadie está leyendo. Abierta se muestra entera,
                porque ahí sí es la ayuda de lo que se está completando. */}
            {!abierto ? (
              (resumen || descripcion) && (
                <span className="mt-0.5 block truncate text-sm text-neutral-500">
                  {resumen || descripcion}
                </span>
              )
            ) : (
              descripcion && (
                <span className="mt-0.5 block text-sm text-neutral-500">{descripcion}</span>
              )
            )}
          </span>

          <ChevronDown
            className={cn(
              'h-5 w-5 shrink-0 text-neutral-400 transition-transform duration-200',
              abierto && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
      </h3>

      {/* Se abre y se cierra con una animación de alto.
          
          Va por JavaScript (framer) y no por CSS a propósito: el panel tiene una
          regla `.dashboard-container * { transition: … !important }` que fija qué
          propiedades transicionan, y el alto no está en esa lista. Una animación
          en CSS quedaría anulada ahí adentro y funcionaría solo en el onboarding.
          Framer escribe el estilo cuadro a cuadro, así que no depende de eso.

          `height: auto` en el destino: framer lo mide y lo convierte en píxeles
          para la animación. Con un `max-height` inventado, un paso corto tarda de
          más y uno largo se corta.

          Sigue desmontándose al cerrar —ahora al terminar la salida— porque si
          quedara en el DOM sus campos seguirían recibiendo foco con el tabulador
          aunque no se vean.

          El relleno va en el div de adentro y no en el que anima: si estuviera
          acá, al cerrarse el alto llegaría a 0 pero el relleno seguiría ocupando
          y el cierre daría un salto al final. */}
      <AnimatePresence initial={false}>
        {abierto && (
          <motion.div
            key="contenido"
            initial={menosMovimiento ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={menosMovimiento ? undefined : { height: 0, opacity: 0 }}
            transition={
              menosMovimiento
                ? { duration: 0 }
                : { duration: 0.3, ease: [0.32, 0.72, 0, 1] }
            }
            style={{ overflow: 'hidden' }}
          >
            <div id={idContenido} className="border-t border-neutral-100 p-4 sm:p-5">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
